import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { unsubscribeAPI } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserX } from 'lucide-react';

const translations = {
  de: {
    title: 'Abmelden',
    subtitle: 'Wir sind traurig, Sie gehen zu sehen',
    emailLabel: 'E-Mail-Adresse',
    domainLabel: 'Domain',
    confirmButton: 'Abmelden bestätigen',
    processing: 'Verarbeitung...',
    description: 'Durch das Abmelden erhalten Sie keine weiteren E-Mails von dieser Domain.'
  },
  fr: {
    title: 'Se désabonner',
    subtitle: 'Nous sommes tristes de vous voir partir',
    emailLabel: 'Adresse e-mail',
    domainLabel: 'Domaine',
    confirmButton: 'Confirmer la désinscription',
    processing: 'Traitement...',
    description: 'En vous désabonnant, vous ne recevrez plus d\'e-mails de ce domaine.'
  },
  it: {
    title: 'Annulla iscrizione',
    subtitle: 'Siamo tristi di vederti andare',
    emailLabel: 'Indirizzo email',
    domainLabel: 'Dominio',
    confirmButton: 'Conferma cancellazione',
    processing: 'Elaborazione...',
    description: 'Cancellandoti, non riceverai più email da questo dominio.'
  },
  en: {
    title: 'Unsubscribe',
    subtitle: 'We are sad to see you go',
    emailLabel: 'Email Address',
    domainLabel: 'Domain',
    confirmButton: 'Confirm Unsubscribe',
    processing: 'Processing...',
    description: 'By unsubscribing, you will no longer receive emails from this domain.'
  }
};

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [domain, setDomain] = useState(searchParams.get('domain') || '');
  const [language, setLanguage] = useState(searchParams.get('lang') || 'de');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Auto-detect language from browser if not provided
    if (!searchParams.get('lang')) {
      const browserLang = navigator.language.split('-')[0];
      if (['de', 'fr', 'it'].includes(browserLang)) {
        setLanguage(browserLang);
      }
    }
  }, [searchParams]);

  const t = translations[language] || translations.en;

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await unsubscribeAPI.unsubscribe({ email, domain, language });
      navigate('/unsubscribe/confirm?lang=' + language);
    } catch (error) {
      toast.error('Unsubscribe failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_mailcraft-13/artifacts/hs5d8bkj_dommo_logo.png" 
            alt="Dommo Logo" 
            className="h-16 w-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold tracking-tight text-[#183871] mb-2">
            Dommo Swiss Mailer
          </h1>
        </div>

        {/* Unsubscribe Form */}
        <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
          <div className="flex items-center justify-center mb-6">
            <UserX size={48} className="text-[#EF4444]" strokeWidth={1.5} />
          </div>

          <h2 className="text-2xl font-bold text-[#F3F4F6] text-center mb-2">
            {t.title}
          </h2>
          <p className="text-[#9CA3AF] text-center mb-6">
            {t.subtitle}
          </p>

          <form onSubmit={handleUnsubscribe} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#F3F4F6]">
                {t.emailLabel}
              </Label>
              <Input
                id="email"
                data-testid="unsubscribe-email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain" className="text-[#F3F4F6]">
                {t.domainLabel}
              </Label>
              <Input
                id="domain"
                data-testid="unsubscribe-domain"
                type="text"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                required
              />
            </div>

            <div className="p-4 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/20">
              <p className="text-sm text-[#9CA3AF]">
                {t.description}
              </p>
            </div>

            <Button
              type="submit"
              data-testid="confirm-unsubscribe-button"
              disabled={loading}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white"
            >
              {loading ? t.processing : t.confirmButton}
            </Button>
          </form>

          {/* Language Selector */}
          <div className="flex justify-center gap-2 mt-6 pt-6 border-t border-[#2A2D36]">
            {['de', 'fr', 'it', 'en'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === lang
                    ? 'bg-[#183871] text-white'
                    : 'text-[#9CA3AF] hover:bg-[#1F2128]'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
